import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { filter, first, firstValueFrom, map, Observable, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from '@realwear-cloud/shared/ui-kit';
import { HubspotDemoDialogComponent } from '../dialogs/hubspot-demo-dialog/hubspot-demo-dialog.component';
import {
  AppService,
  InstallAppDialogComponent,
  MarketplaceAuth,
  MarketplaceStore
} from '@realwear-cloud/app-marketplace/shared';

@Component({
  selector: 'realwear-cloud-app-detail-shell',
  templateUrl: './app-detail-shell.component.html',
  styleUrls: ['./app-detail-shell.component.scss']
})
export class AppDetailShellComponent extends BaseComponent implements OnInit {
  readonly appId$: Observable<string>;

  constructor(
    private matDialog: MatDialog,
    private marketplaceAuth: MarketplaceAuth,
    activatedRoute: ActivatedRoute,
    private title: Title,
    private router: Router,
    private store: MarketplaceStore,
    private apps: AppService,
    private meta: Meta
  ) {
    super();

    this.appId$ = activatedRoute.paramMap.pipe(map((j) => j.get('id') || ''));

    this.store
      .select((state) => state.categoryRequested)
      .pipe(
        filter((category) => !!category),
        takeUntil(this.destroyed$)
      )
      .subscribe(async (category) => {
        this.store.updateState({ categoryRequested: '' });
        await this.router.navigateByUrl('/browse/' + category);
      });

    this.store
      .select((state) => state.installRequested)
      .pipe(
        filter((installRequested) => !!installRequested),
        takeUntil(this.destroyed$)
      )
      .subscribe(async (installRequested) => {
        const appId = installRequested?.appId;
        if (appId) {
          this.store.updateState({ installRequested: null });
          this.matDialog.open(InstallAppDialogComponent, { width: '800px', data: appId });
        }
      });

    this.store
      .select((state) => state.demoRequested)
      .pipe(
        filter((demoRequested) => !!demoRequested),
        takeUntil(this.destroyed$)
      )
      .subscribe(async (demoRequested) => {
        this.store.updateState({ demoRequested: null });
        this.matDialog.open(HubspotDemoDialogComponent, { width: '500px', data: demoRequested?.packageName });
      });
  }

  async onLoginRequested() {
    datadogRum.addAction('LoginRequested');

    const appId = await firstValueFrom(this.appId$);
    this.marketplaceAuth.authorizeForAppId(appId);
  }

  ngOnInit(): void {
    this.appId$
      .pipe(
        switchMap((appId) => this.apps.fetchFullApp(appId)),
        first()
      )
      .subscribe((appInformation) => {
        this.meta.addTag({
          property: 'og:title',
          content: `Discover ${appInformation?.title || 'Unknown'} on the RealWear App Marketplace`
        });

        const ogImage = `https://rwcloudappmarketplace.blob.core.windows.net/appscreenshots/${appInformation?.id}-ogimage.png`;

        // Replace the og:image meta tag
        const ogImageTag = this.meta.getTag(`property='og:image'`);
        if (ogImageTag) {
          ogImageTag.setAttribute('content', ogImage);
        } else {
          this.meta.addTag({ property: 'og:image', content: ogImage });
        }

        this.title.setTitle(`${appInformation?.title || 'Unknown'} - RealWear App Marketplace`);
      });
  }
}
